import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Checkbox, FormControl, FormControlLabel, FormGroup, FormLabel } from '@material-ui/core';
import { Publish } from '@material-ui/icons';
import { LoadingButton } from 'components/buttons';
import { CustomLabel } from 'components/inputs';
import { OFFER_API_LENDER_ENABLED } from 'core/constants';
import { ApiLender, SelectOption } from 'core/types';
import { gatherOffers as gatherOffersApi } from 'http/admin';
import { useAdmin } from 'store/admin/hooks';
import { LenderOffer } from 'store/applications/types';
import { useLenders } from 'store/lenders/hooks';
import { toTitleCase } from 'utils';
import useStyles from './GatherOffers.styles';

const getLendersStr = (apiLenders: ApiLender[]) => {
  const lendersLength = apiLenders.length;
  return apiLenders
    .map((lender, index) => {
      if (lendersLength > 1) {
        if (index < lendersLength - 2) {
          return `${toTitleCase(lender)}, `;
        }
        if (index < lendersLength - 1) {
          return `${toTitleCase(lender)} and  `;
        }
      }
      return toTitleCase(lender);
    })
    .join('');
};

interface GatherOffersProps {
  companyId: string;
  offers: LenderOffer[];
}

const GatherOffers: FC<GatherOffersProps> = ({ companyId, offers }) => {
  const [loading, setLoading] = useState(false);
  const [apiLenders, setApiLenders] = useState<ApiLender[]>([]);
  const classes = useStyles();
  const { t } = useTranslation();
  const { setError, setSuccess } = useAdmin();
  const { allLenders } = useLenders();

  type LenderOption = SelectOption<ApiLender> & {
    id: string;
  };

  const availableApiLenders = useMemo<LenderOption[]>(() => {
    const lendersByName = new Map(allLenders.map((lender) => [lender.name, lender]));

    return Object.values(ApiLender).reduce((acc: LenderOption[], val: ApiLender) => {
      const apiLender = lendersByName.get(val);
      if (!apiLender) return acc;

      acc.push({
        label: apiLender.displayed_name,
        value: val,
        id: apiLender.id,
      });

      return acc;
    }, []);
    // eslint-disable-next-line
  }, [allLenders]);

  const enabledApiLenderValues = useMemo(() => {
    const disabledLenderIds = new Set(
      offers.filter((offer) => !OFFER_API_LENDER_ENABLED[offer.status]).map((offer) => offer.lender_id.toLowerCase()),
    );

    return new Set(
      availableApiLenders
        .filter((lender) => !disabledLenderIds.has(lender.id.toLowerCase()))
        .map((lender) => lender.value),
    );
  }, [availableApiLenders, offers]);

  const onChange = (value: ApiLender) => {
    setApiLenders((prevApiLenders) => {
      const alreadySelected = prevApiLenders.find((lender) => lender === value);
      if (alreadySelected) return prevApiLenders.filter((lender) => lender !== value);
      return [...prevApiLenders, value];
    });
  };

  const onSubmit = async () => {
    setLoading(true);
    try {
      await gatherOffersApi(companyId, apiLenders);
      const successMessage = t('pages.lead.admin.gatherOffers.messages.success', {
        lenders: getLendersStr(apiLenders),
      });
      setSuccess(successMessage);
    } catch (err) {
      setError((err as Error)?.message ?? true);
    }
    setLoading(false);
  };

  return (
    <Box paddingRight="8px">
      <FormControl>
        <FormLabel component={() => <CustomLabel title={t('pages.lead.admin.gatherOffers.title')} />} />
        <FormLabel component={() => <CustomLabel title={t('pages.lead.admin.gatherOffers.subtitle')} />} />

        <FormGroup>
          {availableApiLenders.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={
                <Checkbox
                  checked={apiLenders.includes(option.value)}
                  onChange={() => onChange(option.value)}
                  color="primary"
                  disabled={!enabledApiLenderValues.has(option.value)}
                />
              }
              label={option.label}
              className={classes.controlLabel}
            />
          ))}
        </FormGroup>
      </FormControl>

      <LoadingButton
        variant="contained"
        color="primary"
        disableElevation
        className={classes.button}
        onClick={onSubmit}
        disabled={!apiLenders.length}
        loading={loading}
        startIcon={<Publish />}
      >
        {t('pages.lead.admin.actions.gatherOffers')}
      </LoadingButton>
    </Box>
  );
};

export default GatherOffers;
